import { useContext } from "react";
import { AppContext } from "../App";
import { hospitalInfo } from "../content/hospitalContent";
import { MdOutlineArrowOutward } from "react-icons/md";
import { 
  FiShield, FiUsers, FiActivity, FiTrendingUp, 
  FiLayers, FiCpu, FiTarget, FiZap, 
  FiCheckCircle, FiEye, FiCompass, FiAward,
  FiGlobe, FiHeart, FiSettings, FiRefreshCw
} from "react-icons/fi";

// Asset Imports
import banner1 from "../assets/banner1.jpg";
import banner2 from "../assets/banner2.jpg";
import { IoMdArrowForward, IoMdHeart } from "react-icons/io";
import { FaKitMedical } from "react-icons/fa6";

export default function AboutUsContent() {
  const appContext = useContext(AppContext);

  return (
    <>
      
      <section className="au-section">
        <div className="au-container">
          
          {/* ── Section 01: Hero & Purpose ── */}
          <div className="au-hero">
            <div className="au-visual-stack">
              <div className="au-img-wrapper">
                <img src={banner1} className="au-img-main" alt="Nanguba Healthcare Facility" />
              </div>
              <div className="au-floating-card">
                <h4 style={{ margin: '0 0 8px 0', fontSize: '1.2rem' }}>Digital-First</h4>
                <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--slate-500)' }}>
                  100% Digital integration from Day 1 using POWERHMS across all tiers.
                </p>
              </div>
            </div>

            <div className="au-content">
              <span className="au-mission-tag">Institutional Purpose</span>
              <h1 className="au-title">
                Accessible Care. <br/>
                <span>Patient-Centered.</span>
              </h1>
              <p className="au-body-text">
                Nanguba Healthcare establishes a technology-enabled network built to transform health outcomes 
                across Tanzania. We are not merely opening facilities; we are building an institution.
              </p>
              <a href={hospitalInfo.scheduleUrl} className="btn-clin-primary">
                Network Vision <MdOutlineArrowOutward size={20} />
              </a>
            </div>
          </div>

          {/* ── Section: Mission & Vision Cards ── */}
          <div className="au-mv-grid">
            <div className="mv-card mission">
              <FiCompass className="mv-icon-bg" />
              <span className="mv-label">The Mission</span>
              <p className="mv-content">
                To deliver accessible, high-quality healthcare across clinics, hospitals, and medical centers—using integrated systems and ethical practice to improve outcomes for all.
              </p>
            </div>
            <div className="mv-card vision">
              <FiEye className="mv-icon-bg" />
              <span className="mv-label">The Vision</span>
              <p className="mv-content">
                To become a leading technology-driven network in Tanzania—recognized for clinical excellence and setting the benchmark for community-focused healthcare in Africa.
              </p>
            </div>
          </div>

          {/* ── NEW SECTION: Core Values ── */}
          <div className="values-header">
            <span className="au-mission-tag">Our Standards</span>
            <h2 style={{fontSize: '2.5rem', letterSpacing: '-0.02em', margin: '0 0 15px 0'}}>Core Values</h2>
            <p style={{color: 'var(--slate-500)', maxWidth: '700px', margin: '0 auto'}}>
              Principles that govern behaviour in difficult moments. At Nanguba Healthcare, 
              a value with a commitment is a standard.
            </p>
          </div>

          <div className="values-grid">
            <div className="value-item">
              <FiGlobe className="value-icon" />
              <h4 className="value-name">Accessibility</h4>
              <p className="value-commitment">Healthcare that reaches everyone via multi-tier facilities and telemedicine outreach.</p>
            </div>
            <div className="value-item">
              <FiShield className="value-icon" />
              <h4 className="value-name">Integrity</h4>
              <p className="value-commitment">Evidence-based diagnosis and transparent billing. Zero tolerance for data manipulation.</p>
            </div>
            <div className="value-item">
              <FiHeart className="value-icon" />
              <h4 className="value-name">Patient-Centered</h4>
              <p className="value-commitment">Prioritizing safety and dignity. Door-to-doctor time tracked as a daily critical KPI.</p>
            </div>
            <div className="value-item">
              <FiUsers className="value-icon" />
              <h4 className="value-name">Community Impact</h4>
              <p className="value-commitment">Actively improving public health beyond walls via quarterly screening camps.</p>
            </div>
            <div className="value-item">
              <FiAward className="value-icon" />
              <h4 className="value-name">Excellence</h4>
              <p className="value-commitment">Pursuing continuous improvement. Mandatory CME for all clinical staff (min 20 hrs/yr).</p>
            </div>
            <div className="value-item">
              <FiCpu className="value-icon" />
              <h4 className="value-name">Innovation</h4>
              <p className="value-commitment">Leveraging AI diagnostics and fully integrated EMR systems across all network nodes.</p>
            </div>
            <div className="value-item">
              <FiRefreshCw className="value-icon" />
              <h4 className="value-name">Sustainability</h4>
              <p className="value-commitment">Building a system that lasts—financially, operationally, and environmentally for future generations.</p>
            </div>
            <div className="value-item">
              <FiSettings className="value-icon" />
              <h4 className="value-name">Accountability</h4>
              <p className="value-commitment">Transparent reporting to the board and a 12-KPI dashboard reviewed weekly.</p>
            </div>
          </div>

          {/* ── Section: Strategic Pillars ── */}
          <div style={{textAlign: 'center', marginBottom: '60px'}}>
             <h2 style={{fontSize: '2.5rem', letterSpacing: '-0.02em'}}>Strategic Pillars</h2>
             <p style={{color: 'var(--slate-500)'}}>Structural load-bearers of the Nanguba Healthcare model.</p>
          </div>
          <div className="au-values-grid">
            <div className="au-val-card">
              <div className="au-val-icon"><FiLayers /></div>
              <h3 className="au-val-title">Integrated Network</h3>
              <p className="au-val-desc">Seamless multi-tier referral system (Clinics → Hospitals → Medical Centres) unified by a single record.</p>
            </div>
            <div className="au-val-card">
              <div className="au-val-icon"><FiCpu /></div>
              <h3 className="au-val-title">Digital Infrastructure</h3>
              <p className="au-val-desc">Fully integrated HIMS operational from Day 1. AI-assisted diagnostics and real-time KPI dashboards.</p>
            </div>
            <div className="au-val-card">
              <div className="au-val-icon"><FiTarget /></div>
              <h3 className="au-val-title">Preventive Focus</h3>
              <p className="au-val-desc">Proactive chronic disease management with automated adherence tracking and community outreach.</p>
            </div>
            <div className="au-val-card">
              <div className="au-val-icon"><FiShield /></div>
              <h3 className="au-val-title">Financial Inclusivity</h3>
              <p className="au-val-desc">Tiered pricing, mobile money payments, and NHIF integration ensuring access for all income levels.</p>
            </div>
            <div className="au-val-card">
              <div className="au-val-icon"><FiUsers /></div>
              <h3 className="au-val-title">Workforce Excellence</h3>
              <p className="au-val-desc">Performance-based incentives, clear clinical governance, and mandatory continuous staff training (CME).</p>
            </div>
            <div className="au-val-card" style={{background: 'var(--slate-100)'}}>
              <div className="au-val-icon"><FiTrendingUp /></div>
              <h3 className="au-val-title">Sustainability</h3>
              <p className="au-val-desc">Building a system that lasts—financially, operationally, and environmentally for future generations.</p>
            </div>
          </div>

          {/* ── Strip & Care Section ── */}
          <div className="au-bottom-strip">
             <div className="au-strip-image-card">
                <img src={banner2} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Clinical Team" />
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, var(--clinical-navy), transparent)', opacity: 0.75 }}></div>
                <div style={{ position: 'absolute', top: '40px', left: '40px', color: 'white', height:'100px' }}>
                  <h3 className="pe-5" style={{ fontSize: '2rem', margin: 0 }}>Operational Excellence</h3>
                  <p style={{ opacity: 0.8 }}>Quality is not assumed — it is demonstrated with data.</p>
                </div>
             </div>
             <div className="au-strip-teal-card">
                <FaKitMedical size={48} style={{ marginBottom: '24px', color: 'var(--clinical-light)' }} />
                <h3 style={{ fontSize: '1.5rem', marginBottom: '16px', fontWeight: '700' }}>
                  Nanguba Care
                </h3>
                <p style={{ fontSize: '1.0rem', lineHeight: '1.6', opacity: 0.9 }}>
                  Affordable peace of mind for your family. Enjoy consistent, high-quality 
                  preventive care through a simple monthly membership designed to keep you healthy, not just treat you when you're ill.
                </p>
                <div className="mt-3">
                  <button className="btn btn-light px-4" onClick={()=>appContext.navTo({item:'ncp'})}>
                    Explore <IoMdArrowForward size={32} />
                  </button>
                </div>
              </div>
          </div>

        </div>
      </section>
    </>
  );
}